"use client";
/**
 * Admin-UI translation hooks. Kept deliberately light: the ~75 KB of admin-UI
 * string tables (`admin-locales/*`) are imported only by
 * `admin-i18n-provider.tsx` (mounted under /admin) and reach `useT` through
 * context, so the public site — which imports `useT` transitively via the block
 * attribute components and the module client API — never ships them.
 *
 * Module content strings (`installedModuleLocales`) stay bundled here because
 * module *site* blocks localise their own chrome on the public site.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { installedModuleLocales } from "@/modules/registry-locales.generated";

/** Selected admin UI language. */
export const UiLocaleCtx = createContext<string>("nl");

/** Admin-UI string tables, keyed by locale. Populated by AdminI18nProvider. */
export const AdminStringsCtx = createContext<Record<string, Record<string, string>>>({});

/**
 * Content language selected in the admin sidebar: which language of pages,
 * posts, forms etc. lists display. Independent from the UI language.
 */
const ContentCtx = createContext<{ locale: string; setLocale: (l: string) => void; locales: string[] }>({
  locale: "nl",
  setLocale: () => {},
  locales: ["nl"],
});

export function ContentLocaleProvider({ initial, locales = ["nl"], children }: { initial: string; locales?: string[]; children: ReactNode }) {
  const [locale, setLocaleState] = useState(initial);
  useEffect(() => {
    const stored = localStorage.getItem("cms-content-locale");
    if (stored && locales.includes(stored)) setLocaleState(stored);
    // Enabled locales are fixed for the session; only read the stored value once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const setLocale = (l: string) => {
    setLocaleState(l);
    localStorage.setItem("cms-content-locale", l);
  };
  return <ContentCtx.Provider value={{ locale, setLocale, locales }}>{children}</ContentCtx.Provider>;
}

export function useContentLocale() {
  return useContext(ContentCtx);
}

function interpolate(value: string, params?: Record<string, string | number>): string {
  return params ? value.replace(/\{(\w+)\}/g, (match, name) => String(params[name] ?? match)) : value;
}

export function useT(): (key: string, params?: Record<string, string | number>) => string {
  const locale = useContext(UiLocaleCtx);
  const admin = useContext(AdminStringsCtx);
  return (key, params) => interpolate(
    installedModuleLocales[locale]?.[key]
      || admin[locale]?.[key]
      || installedModuleLocales.nl?.[key]
      || admin.nl?.[key]
      || key,
    params,
  );
}
